import { useCallback, useContext, useMemo } from 'react'

import { type FrameBuilderPassepartoutColor } from '../../../lib/state'
import { FrameBuilderConfigurationContext } from '../../../contexts/configuration-context'
import { FrameBuilderSelectedStateContext } from '../../../contexts/state-context'
import { FrameBuilderStringsContext } from '../../../contexts/strings-context'
import { FrameBuilderValidationContext } from '../../../contexts/validation-context'

import ListContainer from '../../utility/list-container'
import SmallGraphic from '../../utility/small-graphic'

interface PassepartoutSizeDetailsProps {
  passepartoutSizeId: string
}

const PassepartoutSizeDetails = ({
  passepartoutSizeId,
}: PassepartoutSizeDetailsProps) => {
  const { selectedState, dispatchSelectedState } = useContext(
    FrameBuilderSelectedStateContext,
  )
  const { enabledItems, lastValidState } = useContext(
    FrameBuilderValidationContext,
  )
  const frameBuilderStrings = useContext(FrameBuilderStringsContext)
  const configuration = useContext(FrameBuilderConfigurationContext)

  const passepartoutSize = useMemo(
    () =>
      configuration.passepartout.sizes.find(
        (size) => size._id === passepartoutSizeId,
      ),
    [configuration.passepartout.sizes, passepartoutSizeId],
  )
  const passepartoutColors = useMemo(
    () =>
      configuration.passepartout.colors.filter(
        (passepartoutColor) =>
          !selectedState.profile ||
          !!passepartoutColor.availableFor?.some(
            (linkedProfile) => linkedProfile._id === selectedState.profile?._id,
          ),
      ),
    [configuration.passepartout.colors, selectedState.profile],
  )

  const selectColor = useCallback(
    (passepartoutColor: FrameBuilderPassepartoutColor) => {
      dispatchSelectedState({
        type: 'passepartout',
        color: passepartoutColor,
      })
    },
    [dispatchSelectedState],
  )

  if (
    !passepartoutSize ||
    (passepartoutSize.size === 0 && !passepartoutSize.description)
  ) {
    return
  }

  return (
    <div className="w-full border-t border-gray-300 pt-3 pb-4 px-4">
      {!!passepartoutSize.description && (
        <div className="flex text-xs text-gray-500 leading-4">
          {passepartoutSize.description}
        </div>
      )}

      {passepartoutSize.size > 0 && (
        <>
          <div className="flex flex-col">
            {!!frameBuilderStrings.passepartoutColorLabel && (
              <span>{frameBuilderStrings.passepartoutColorLabel}</span>
            )}

            <ListContainer gapSize="xs" className="mt-2">
              {passepartoutColors.map((passepartoutColor) => (
                <SmallGraphic
                  key={passepartoutColor._id}
                  backgroundColor={passepartoutColor.color?.hex}
                  selected={
                    passepartoutColor._id ===
                    selectedState.passepartout.color?._id
                  }
                  error={
                    lastValidState.passepartout.color?._id ===
                    passepartoutColor._id
                  }
                  disabled={
                    !enabledItems.passepartoutColorIds.includes(
                      passepartoutColor._id,
                    )
                  }
                  onClick={() => selectColor(passepartoutColor)}
                />
              ))}
            </ListContainer>
          </div>

          {!!selectedState.passepartout.color && (
            <p className="mt-4 text-gray-500 leading-6">
              {selectedState.passepartout.color.title}
            </p>
          )}
        </>
      )}
    </div>
  )
}

export default PassepartoutSizeDetails
